/**
 * @file manualPlayerUtils.ts
 * @description Utility functions for deriving display values from manual players.
 * Manual players lack bioData.age and bioData.roleArchetype; these helpers
 * fall back to Player-level dateOfBirth and positions respectively.
 * @module player-kanban
 */

import { differenceInMonths, differenceInYears } from "date-fns";

/**
 * Minimal shape needed to derive a player's display age.
 * Matches both KanbanPlayerDataFragment and PlayerKanbanPlayerFragmentFragment.
 */
interface PlayerWithAge {
  readonly bioData?: { readonly age?: number | null } | null;
  readonly dateOfBirth?: string | null;
}

/**
 * Minimal shape needed to derive a player's display team name.
 * Matches both KanbanPlayerDataFragment and PlayerKanbanPlayerFragmentFragment.
 */
interface PlayerWithTeam {
  readonly team?: { readonly name?: string | null } | null;
  readonly teamName?: string | null;
}

/**
 * Minimal shape needed to derive a player's contract months remaining.
 */
interface PlayerWithContract {
  readonly bioData?: { readonly contractExpires?: string | null } | null;
}

/**
 * Minimal shape needed to derive a player's display archetype.
 */
interface PlayerWithArchetype {
  readonly bioData?: { readonly roleArchetype?: string | null } | null;
  readonly positions?: readonly { readonly name: string }[] | null;
}

/**
 * Derive display age from bioData.age or dateOfBirth for manual players
 * @param player - Player data with optional bioData.age and dateOfBirth
 * @returns Numeric age, or 0 when neither source is available
 * @remarks Manual players have bioData: null; dateOfBirth on the Player root
 * is the only age source for them.
 */
export const getPlayerAge = (player: PlayerWithAge): number => {
  const bioAge = player.bioData?.age;
  if (bioAge != null) return bioAge;
  return player.dateOfBirth
    ? differenceInYears(new Date(), new Date(player.dateOfBirth))
    : 0;
};

/**
 * Derive display team name from team entity or free-text teamName for manual players
 * @param player - Player data with optional team entity and teamName string
 * @param fallback - Value to return when neither source is available
 * @returns Team name string or the provided fallback
 * @remarks Manual players store team as free-text teamName (not a linked team
 * entity), so this helper checks both sources.
 */
export const getPlayerTeamName = (
  player: PlayerWithTeam,
  fallback = ""
): string => player.team?.name ?? player.teamName ?? fallback;

/**
 * Derive display archetype from bioData.roleArchetype or first position name
 * @param player - Player data with optional bioData.roleArchetype and positions
 * @param fallback - Value to return when neither source is available
 * @returns Archetype string or the provided fallback
 * @remarks Manual players lack roleArchetype; their positions array is the
 * only source for position/archetype display.
 */
export const getPlayerArchetype = (
  player: PlayerWithArchetype,
  fallback = ""
): string =>
  player.bioData?.roleArchetype || player.positions?.[0]?.name || fallback;

/**
 * Minimal shape needed to derive a player's display POSITION.
 * @remarks
 * Deliberately separate from {@link PlayerWithArchetype}: the archetype rule
 * reads `roleArchetype` first, which is a playing-style classification, not a
 * position. Sharing one shape would invite reusing the wrong rule.
 */
interface PlayerWithPosition {
  readonly bioData?: { readonly primaryPosition?: string | null } | null;
  readonly positions?:
    | readonly {
        readonly name: string;
        readonly abbreviation?: string | null;
      }[]
    | null;
}

/**
 * Derive the player's display position (SE-5492).
 * @remarks
 * Precedence: the `abbreviation` of the `positions[]` entry that NAMES the
 * `bioData.primaryPosition` → that SAME entry's full `name` → first
 * position's `abbreviation` → first position's `name`. Manual players have
 * `bioData: null`, so the `positions` array is their only source — the same
 * reason {@link getPlayerArchetype} falls back to it.
 *
 * **Why the first step exists (SE-6554).** `primaryPosition` is a full
 * position NAME (`"Right Centre Midfielder"`), and the card's chip is a
 * fixed-width badge, so the long form both overflowed the chip and pushed the
 * `club · age · contract` row into truncating (`"Arsenal · 2…"`). The design
 * shows the abbreviation (`RCM`). The abbreviation is not invented here: it is
 * read off the SAME position the player's `primaryPosition` names, so the chip
 * still identifies the primary position and merely renders its short form.
 * When that position carries no `abbreviation` the full name is kept rather
 * than substituting a DIFFERENT position's short code.
 *
 * **Why an unmatched `primaryPosition` falls through to `positions[0]`
 * (SE-8168).** `bioData.primaryPosition` is free text and can disagree with
 * the player's own `positions[]` list (an out-of-vocabulary/stale value in
 * the same family as SE-6523 and DAT-135) — confirmed live on staging for
 * Enzo Fernández, whose `primaryPosition` read `"Left Defensive Midfielder"`
 * while `positions` held only `"Right Defensive Midfielder"`. The OLD code
 * rendered that unmatched string verbatim: wrong side AND unabbreviated.
 * Every other surface in this codebase that shows a position (the player
 * profile header's `getPlayerPositions`, scouting search rows, transfer
 * report rows) never reads `primaryPosition` at all — `positions[0]` is
 * always the trusted source. So when NO entry in `positions[]` names
 * `primaryPosition`, that string is unverified and is discarded in favor of
 * the same `positions[0]` fallback used when `primaryPosition` is absent,
 * rather than rendered as-is.
 *
 * Returns `undefined` (not `""`) when no source exists, so a caller can
 * distinguish "no position known" from "position is empty" and skip rendering
 * the field entirely rather than showing a blank label.
 * @param player - Player data with optional bioData.primaryPosition and positions
 * @returns The position string, or `undefined` when no source is available
 */
export const getPlayerPosition = (
  player: PlayerWithPosition
): string | undefined => {
  const primary = player.bioData?.primaryPosition?.trim();

  if (primary) {
    const named = player.positions?.find(
      candidate =>
        candidate.name?.trim().toLowerCase() === primary.toLowerCase()
    );
    // Only trust `primary` as display text when some position entry actually
    // corroborates it. An unmatched `primary` is discarded, not rendered —
    // see the SE-8168 remark above.
    if (named) {
      return named.abbreviation?.trim() || primary;
    }
  }

  const first = player.positions?.[0];
  const abbreviation = first?.abbreviation?.trim();
  if (abbreviation) return abbreviation;

  const name = first?.name?.trim();
  return name || undefined;
};

/**
 * Derive contract months remaining from bioData.contractExpires
 * @param player - Player data with optional bioData.contractExpires
 * @returns Months remaining (clamped to 0), or null when no contract data
 * @remarks Returns null for manual players that lack contractExpires,
 * ensuring "N/A" display instead of a misleading number.
 */
export const getPlayerContractMonths = (
  player: PlayerWithContract
): number | null => {
  const contractExpires = player.bioData?.contractExpires;
  if (!contractExpires) return null;

  const expiryDate = new Date(contractExpires);
  if (Number.isNaN(expiryDate.getTime())) return null;

  return Math.max(0, differenceInMonths(expiryDate, new Date()));
};
